import { formatAED, type RecurringFee, type RecurringFeeInterval } from "@teamna/shared";
import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Callout,
  Card,
  ChipRow,
  EmptyState,
  ErrorState,
  FactRow,
  InlineError,
  OwnerListRow,
  RepeatIcon,
  OwnerSectionHeader,
  Sheet,
  SkeletonList,
  SuccessNotice,
  type ChipOption,
} from "../components/index.ts";
import {
  useActiveBusiness,
  useOwnerGroups,
  useOwnerEnrollments,
  useOwnerRecurringFees,
  useOwnerScheduleTemplates,
  useUpdateRecurringFee,
} from "@teamna/web-client/hooks/use-owner.ts";
import { formatDate, formatLocalTime, weekdayName } from "@teamna/web-client/lib/format.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { OwnerGate, useOwnerNotice, type OwnerNotice } from "./shell.tsx";

/**
 * Standing fees, which is the swimming pilot's entire revenue model.
 *
 * V1 is request and remind rather than auto-charge: a daily job raises a payment request ahead of
 * the due date and walks a reminder ladder, and the member pays in the app. So the owner's job on
 * this screen is to see who is on a fee, what it is worth, when it next falls due, and to stop one
 * when a child leaves. Setting prices is a setup task and is not here.
 *
 * The month total is the first thing on the screen because it is the number the owner actually
 * opens this tab for. A fee hangs off an enrollment rather than a member, so the name on each row
 * comes from the enrollments list: one member can hold two slots at two different prices.
 *
 * Owner only. Staff never see money, and the API returns 403 rather than trusting the UI to hide it.
 */
export function OwnerFeesScreen() {
  const { business, managed, isLoading, error, select, isOwner } = useActiveBusiness();
  const businessId = business?.businessId ?? null;

  const chips = useMemo<ChipOption[]>(
    () => managed.map((row) => ({ id: row.businessId, label: row.name })),
    [managed],
  );

  return (
    <OwnerGate
      title="Payments"
      business={business}
      isLoading={isLoading}
      error={error}
      managedCount={managed.length}
    >
      {chips.length > 1 ? (
        <div className="pb-4">
          <ChipRow
            options={chips}
            selectedId={business?.businessId ?? ""}
            onSelect={select}
            label="Which business to manage"
          />
        </div>
      ) : null}

      {!isOwner ? (
        <EmptyState
          title="Owner only"
          message="Fees are visible to the business owner. Ask them if you need a figure."
        />
      ) : businessId ? (
        <RecurringFeesBody businessId={businessId} />
      ) : null}
    </OwnerGate>
  );
}

/**
 * The standing fees of one business: the month total, the running and stopped fees, and the sheet
 * that stops or restarts one. The Payments page shows it under its "Recurring fees" tab.
 */
export function RecurringFeesBody({ businessId }: { businessId: string }) {
  const fees = useOwnerRecurringFees(businessId, true);
  const enrollments = useOwnerEnrollments(businessId);
  const templates = useOwnerScheduleTemplates(businessId);
  const groups = useOwnerGroups(businessId);
  const { label } = useLabels();

  const [selected, setSelected] = useState<RecurringFee | null>(null);
  const notice = useOwnerNotice();

  /** enrollment id to who it is for, which slot, and whether that slot has already ended. */
  const payer = useMemo(() => {
    const className = new Map((groups.data ?? []).map((row) => [row.id, row.name]));
    const slotName = new Map(
      (templates.data ?? []).map((row) => [
        row.id,
        `${weekdayName(row.weekday)} ${formatLocalTime(row.startTime)} · ${className.get(row.groupId) ?? label("group", { capital: true })}`,
      ]),
    );
    return new Map(
      (enrollments.data ?? []).map((row) => [
        row.id,
        {
          who: row.participantName || label("participant", { capital: true }),
          slot: slotName.get(row.templateId) ?? "Weekly slot",
          ended: row.status !== "active",
        },
      ]),
    );
  }, [enrollments.data, templates.data, groups.data, label]);

  const list = fees.data ?? [];
  const running = list.filter((fee) => fee.active);
  const stopped = list.filter((fee) => !fee.active);
  const monthlyTotal = running
    .filter((fee) => fee.interval === "monthly")
    .reduce((sum, fee) => sum + fee.amountFils, 0);

  return (
    <div>
      {notice.value ? (
        <SuccessNotice
          title={notice.value.title}
          message={notice.value.message}
          onDismiss={notice.clear}
        />
      ) : null}

      <div className="rounded-card border border-line bg-paper p-5 shadow-card">
        <p className="eyebrow">Every month</p>
        <p className="mt-1 text-[34px] leading-none font-semibold text-ink tabular-nums">
          {formatAED(monthlyTotal)}
        </p>
        <p className="mt-2.5 text-[13px] text-ink-2">
          What the monthly fees add up to when everyone pays.{" "}
          {label("member", { plural: true, capital: true })} are reminded a week ahead, the day before,
          on the day, and three days late.
        </p>
      </div>

      {fees.isLoading && !fees.data ? (
        <div className="pt-6">
          <SkeletonList />
        </div>
      ) : fees.error && !fees.data ? (
        <div className="pt-6">
          <ErrorState error={fees.error} onRetry={() => void fees.refetch()} />
        </div>
      ) : list.length === 0 ? (
        <div className="pt-6">
          <EmptyState
            icon={<RepeatIcon size={30} />}
            title="No recurring fees"
            message="Nobody is on a standing fee yet. A fee is attached to a weekly slot, so it starts with a slot."
          />
        </div>
      ) : (
        <>
          {running.length > 0 ? (
            <>
              <OwnerSectionHeader title="Running" detail={String(running.length)} />
              <Card>
                {running.map((fee, index) => (
                  <FeeRow
                    key={fee.id}
                    fee={fee}
                    payer={payer.get(fee.enrollmentId)}
                    onSelect={() => setSelected(fee)}
                    last={index === running.length - 1}
                  />
                ))}
              </Card>
            </>
          ) : null}

          {stopped.length > 0 ? (
            <>
              <OwnerSectionHeader title="Stopped" detail={String(stopped.length)} />
              <Card>
                {stopped.map((fee, index) => (
                  <FeeRow
                    key={fee.id}
                    fee={fee}
                    payer={payer.get(fee.enrollmentId)}
                    onSelect={() => setSelected(fee)}
                    last={index === stopped.length - 1}
                  />
                ))}
              </Card>
            </>
          ) : null}
        </>
      )}

      {selected ? (
        <FeeSheet
          key={selected.id}
          fee={selected}
          businessId={businessId}
          payer={payer.get(selected.enrollmentId)}
          onClose={() => setSelected(null)}
          onDone={(value) => {
            notice.show(value);
            setSelected(null);
          }}
        />
      ) : null}
    </div>
  );
}

type Payer = { who: string; slot: string; ended: boolean };

function FeeRow({
  fee,
  payer,
  onSelect,
  last,
}: {
  fee: RecurringFee;
  payer?: Payer;
  onSelect: () => void;
  last: boolean;
}) {
  const due = fee.active ? `next due ${formatDate(fee.nextDueOn)}` : "stopped";
  return (
    <OwnerListRow
      title={payer?.who ?? "Not known"}
      subtitle={`${payer?.slot ?? "Weekly slot"} · ${cadenceWord(fee.interval)}, ${due}`}
      value={formatAED(fee.amountFils)}
      muted={!fee.active}
      onSelect={onSelect}
      last={last}
      trailing={fee.active ? undefined : <Badge tone="quiet">Stopped</Badge>}
    />
  );
}

/**
 * One fee, and the only thing an owner does to one: stop it, or start it again.
 *
 * The confirm step spells out what stopping does and, more usefully, what it does not do. An owner
 * stopping a fee is usually worried about the invoice they already sent, and the answer is that it
 * stays on the member's account.
 */
function FeeSheet({
  fee,
  businessId,
  payer,
  onClose,
  onDone,
}: {
  fee: RecurringFee;
  businessId: string;
  payer?: Payer;
  onClose: () => void;
  onDone: (notice: OwnerNotice) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const updateFee = useUpdateRecurringFee(businessId);

  const { label } = useLabels();
  const who = payer?.who ?? `this ${label("participant")}`;
  const next = !fee.active;

  return (
    <Sheet
      open
      onClose={onClose}
      title={confirming ? (next ? `Start ${who}'s fee again?` : `Stop ${who}'s fee?`) : who}
      subtitle={confirming ? undefined : (payer?.slot ?? "Weekly slot")}
      actions={
        confirming ? (
          <>
            <Button
              size="lg"
              fullWidth
              variant={next ? "primary" : "secondary"}
              loading={updateFee.isPending}
              onClick={async () => {
                setError(null);
                try {
                  await updateFee.mutateAsync({ feeId: fee.id, active: next });
                  onDone({
                    title: next ? "Fee started again" : "Fee stopped",
                    message: next
                      ? `${who} goes back on ${formatAED(fee.amountFils)} ${cadenceWord(fee.interval)}, next due ${formatDate(fee.nextDueOn)}.`
                      : `No more requests or reminders go out for ${who}. Anything already raised stays on their account.`,
                  });
                } catch (caught) {
                  setError(caught);
                }
              }}
            >
              {next ? "Start it again" : "Stop the fee"}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => setConfirming(false)}>
              Back
            </Button>
          </>
        ) : (
          <>
            <Button
              size="lg"
              fullWidth
              variant={fee.active ? "secondary" : "primary"}
              onClick={() => {
                setError(null);
                setConfirming(true);
              }}
            >
              {fee.active ? "Stop this fee" : "Start this fee again"}
            </Button>
            <Button variant="ghost" fullWidth onClick={onClose}>
              Close
            </Button>
          </>
        )
      }
    >
      {error ? <InlineError error={error} /> : null}

      {confirming ? (
        <Callout tone={next ? "quiet" : "late"} title={next ? "What starting does" : "What stopping does"}>
          {next
            ? `Requests and reminders start again on ${formatDate(fee.nextDueOn)}, at ${formatAED(fee.amountFils)} ${cadenceWord(fee.interval)}.`
            : "No more requests or reminders go out for this fee. Anything already raised and unpaid stays on their account, and you can start it again later."}
        </Callout>
      ) : (
        <div>
          <FactRow label="Amount" value={formatAED(fee.amountFils)} />
          <FactRow label="How often" value={cadenceLabel(fee.interval)} />
          <FactRow
            label="Next due"
            value={fee.active ? formatDate(fee.nextDueOn) : "Not scheduled"}
          />
          <FactRow label="Status" value={fee.active ? "Running" : "Stopped"} last />
          {payer?.ended ? (
            <p className="pt-4 text-[13px] text-ink-2">
              The weekly slot behind this fee is no longer active, so it is probably safe to stop the
              fee too.
            </p>
          ) : null}
        </div>
      )}
    </Sheet>
  );
}

/** "Monthly" or "Every term", as a label. */
function cadenceLabel(interval: RecurringFeeInterval): string {
  return interval === "monthly" ? "Monthly" : "Every term";
}

/** The same cadence mid-sentence. */
function cadenceWord(interval: RecurringFeeInterval): string {
  return interval === "monthly" ? "monthly" : "every term";
}
